import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
    Bucket, KeySelection, RunsResponse, executionStatsService, isAbort
} from '../../services/executionStatsService';
import { PanelError } from '../Benchmark/BenchmarkPrimitives';
import { useBucketColours, BUCKET_LABELS, BUCKET_ORDER, count, duration } from './executionStatsVocabulary';
import { ErrorGroups, StatusPill, when } from './runPresentation';
import { runLogHref } from './runLogLinks';
import { dsVar, ink } from '../../theme/tokens';

export { errorSignature, groupErrors } from './runPresentation';
export type { ErrorGroup } from './runPresentation';

const PAGE_SIZE = 20;

export interface RunListQuery extends KeySelection {
    /** The one key whose runs are listed. */
    key: string;
    from: string;
    to: string;
    bucket: Bucket | null;
}

/**
 * The runs behind a count.
 *
 * The API buckets these with the same expression as the counts, so a count of
 * 3 failed opens a list of the same 3 runs. The bucket chips switch the list
 * in place rather than sending the reader back to pick another count.
 *
 * Styled with sx, not Tailwind classes: it renders inside drawers, which are
 * portalled outside #root where Tailwind's important selector doesn't reach.
 */
const RunList: React.FC<{
    query: RunListQuery;
    /** Buckets offered as chips; untracked ones are left out. */
    buckets?: Bucket[];
    onBucketChange?: (bucket: Bucket | null) => void;
}> = ({ query, buckets = BUCKET_ORDER, onBucketChange }) => {
    const bucketColours = useBucketColours();
    const [page, setPage] = useState(1);
    const [data, setData] = useState<RunsResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [expanded, setExpanded] = useState<string | null>(null);

    const { scope, keyType, key, groupVersions, from, to, bucket } = query;

    useEffect(() => { setPage(1); }, [scope, keyType, key, groupVersions, from, to, bucket]);

    useEffect(() => {
        const controller = new AbortController();
        setError(null);
        executionStatsService
            .listRuns(
                { scope, keyType, keys: [key], groupVersions, from, to, bucket, page, limit: PAGE_SIZE },
                { signal: controller.signal }
            )
            .then(setData)
            .catch((err) => { if (!isAbort(err)) setError(err?.message || 'Could not load runs.'); });
        return () => controller.abort();
    }, [scope, keyType, key, groupVersions, from, to, bucket, page, attempt]);

    const chip = (value: Bucket | null, label: string) => {
        const active = value === bucket;
        return (
            <Box
                key={label}
                component="button"
                aria-pressed={active}
                onClick={() => onBucketChange?.(value)}
                sx={{
                    fontSize: '0.6875rem', fontWeight: 600, px: 1, py: 0.25, borderRadius: '999px', cursor: 'pointer',
                    border: '1px solid', borderColor: active ? dsVar('accent-fg') : dsVar('line'),
                    color: active ? dsVar('on-accent') : dsVar('fg-2'), backgroundColor: active ? dsVar('accent') : 'transparent'
                }}
            >
                {label}
            </Box>
        );
    };

    return (
        <Box>
            {onBucketChange && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    {chip(null, 'All')}
                    {buckets.map((b) => chip(b, BUCKET_LABELS[b]))}
                </Box>
            )}

            {error && <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />}

            {!error && !data && (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>Loading runs…</Typography>
            )}

            {!error && data && data.runs.length === 0 && (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                    No {bucket ? BUCKET_LABELS[bucket].toLowerCase() : ''} runs in this window.
                </Typography>
            )}

            {!error && data && data.runs.length > 0 && (
                <>
                    {page === 1 && <ErrorGroups runs={data.runs} total={data.pagination.total} />}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                            {count(data.pagination.total)} {data.pagination.total === 1 ? 'run' : 'runs'}, newest first
                        </Typography>
                        <Box
                            component="a"
                            href={runLogHref({
                                ...(keyType === 'campaign' ? { campaignId: key } : { code: key, groupVersions }),
                                bucket, from, to
                            })}
                            sx={{ fontSize: '0.6875rem', color: dsVar('accent-fg'), textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Open in Run log →
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {data.runs.map((run, index) => {
                            const id = run.referenceId ?? `row-${index}`;
                            const open = expanded === id;
                            return (
                                <Box
                                    key={id}
                                    sx={{ borderLeft: '2px solid', borderColor: run.bucket ? bucketColours[run.bucket] : dsVar('line'), pl: 1.5 }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), fontWeight: 600 }}>
                                            {when(run.startedAt)}
                                        </Typography>
                                        <StatusPill bucket={run.bucket} status={run.status} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                        {run.completedAt ? `Took ${duration(run.durationMs)}` : 'No finish recorded'}
                                        {run.name ? ` · ${run.name}` : ''}
                                    </Typography>
                                    {run.referenceId && (
                                        <Box
                                            component="a"
                                            href={runLogHref({ run: run.referenceId })}
                                            title="Open this run's steps in the Run log"
                                            sx={{
                                                display: 'block', fontSize: '0.625rem', color: dsVar('fg-muted'), fontFamily: '"JetBrains Mono", monospace',
                                                textDecoration: 'none', '&:hover': { color: dsVar('accent-fg'), textDecoration: 'underline' }
                                            }}
                                        >
                                            {run.referenceId}
                                        </Box>
                                    )}
                                    {run.errorMessage && (
                                        <Typography
                                            component="button"
                                            onClick={() => setExpanded(open ? null : id)}
                                            sx={{
                                                display: 'block', textAlign: 'left', mt: 0.5, p: 0, border: 'none', background: 'none',
                                                cursor: 'pointer', fontSize: '0.6875rem', color: ink('#FCA5A5'), fontFamily: '"JetBrains Mono", monospace',
                                                whiteSpace: open ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                maxWidth: '100%', wordBreak: 'break-word'
                                            }}
                                        >
                                            {run.errorMessage}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                    {data.pagination.pages > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                            {[['Newer', page - 1, page <= 1], ['Older', page + 1, page >= data.pagination.pages]].map(
                                ([label, target, disabled]) => (
                                    <Box
                                        key={String(label)}
                                        component="button"
                                        disabled={Boolean(disabled)}
                                        onClick={() => setPage(Number(target))}
                                        sx={{
                                            fontSize: '0.75rem', color: disabled ? dsVar('fg-faint') : dsVar('accent-fg'), background: 'none',
                                            border: 'none', cursor: disabled ? 'default' : 'pointer', p: 0
                                        }}
                                    >
                                        {label === 'Newer' ? '← Newer' : 'Older →'}
                                    </Box>
                                )
                            )}
                            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                Page {page} of {data.pagination.pages}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default RunList;
